import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'
import { MdOutlineClear } from 'react-icons/md'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'

import type { IAuthentication } from './types/authentication'
import type { ReactElement } from 'react'

import Button from './components/Buttons/Button'
import Router from './Router'
import Login from './views/Login'

import myPackage from '../package.json'

import PipBoy from '~/assets/pipboy.png'
import ChangeLogModal from '~/components/ChangeLogModal'
import DeprecationBanner from '~/components/DeprecationBanner'
import FeedbackButton from '~/components/FeedbackButton'
import Modal from '~/components/Modal'
import { ModalBody } from '~/components/Modal/Layout'
import MwMLoader from '~/components/MwMLoader'
import NavBar from '~/components/NavBar'
import { COOKIES, LOCAL_STORAGE } from '~/const'
import CHANGELOG from '~/const/changelog.json'
import { shortcut } from '~/const/shortcut'
import usePageViewEvent from '~/hooks/usePageViewEvent'
import { useShortcut } from '~/hooks/useShortcut'
import AppSummaryProvider from '~/providers/AppSummary'
import AuthorizationsProvider from '~/providers/Authorizations'
import { SearchParamsProvider } from '~/providers/SearchParams'
import TokenHandler from '~/providers/Token'
import { useGetMeAsUserQuery } from '~/queries/users'
import dayjs from '~/utils/dayjs'
import { isProduction } from '~/utils/env'
import { createRain } from '~/utils/konami'
import { formatNotionUrl } from '~/utils/notion'
import { isTokenValid } from '~/utils/token'

import '@google/workflow-graph/web-component/registerWorkflowGraphWebComponent'
import 'rc-tooltip/assets/bootstrap.css'
import './css/index.scss'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      notifyOnChangeProps: ['data', 'error'],
      refetchOnWindowFocus: false,
      retry: false,
      // globally default to 20 seconds
      staleTime: 1000 * 20,
    },
  },
})

function App(): ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      <AppLogic />
    </QueryClientProvider>
  )
}

const KEY_FOR_EASTER_EGG = ['KeyQ', 'KeyN', 'KeyT']

const AppLogic = (): ReactElement => {
  /** Local state */

  const stabilityBannerValue = localStorage.getItem(LOCAL_STORAGE.STABILITY_BANNER)

  const [cookies, setCookie] = useCookies([COOKIES.TOKENS, COOKIES.CHANGELOG, COOKIES.NEW_VERSION_START_TIME])

  const accessTokens: IAuthentication | undefined = cookies[COOKIES.TOKENS]

  const [isBannerOpen, setIsBannerOpen] = useState(!stabilityBannerValue || stabilityBannerValue === 'true')

  const [changeLogModalIsOpen, setChangeLogModalIsOpen] = useState(false)

  const changelog: Record<string, string> = CHANGELOG

  const currentAppVersion = myPackage.version?.split('.').splice(0, 2).join('.')

  const pageId = formatNotionUrl(changelog[currentAppVersion])

  const { hash, pathname } = useLocation()

  const navigate = useNavigate()

  const [searchParams] = useSearchParams()

  /** Queries & Mutations */

  const { data: user, isLoading } = useGetMeAsUserQuery(isTokenValid(cookies[COOKIES.TOKENS]))

  /** Effects */

  useEffect(() => {
    const cookieVersion = cookies[COOKIES.CHANGELOG]?.split('.').splice(0, 2).join('.')

    if (currentAppVersion !== cookieVersion) {
      setCookie(COOKIES.CHANGELOG, myPackage.version, { path: '/' })
      setCookie(COOKIES.NEW_VERSION_START_TIME, dayjs(new Date()))
      setChangeLogModalIsOpen(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useShortcut({
    ...shortcut.easterEgg,
    fn: (code?: string) => {
      code && setKeySuite((prev) => [...prev, code])
    },
  })

  usePageViewEvent()

  /* ////// Easter Egg for Clara */
  const [keySuite, setKeySuite] = useState<string[]>([])
  const [easterEggModal, setEasterEggModal] = useState(false)

  useEffect(() => {
    if (KEY_FOR_EASTER_EGG.every((r) => keySuite.includes(r))) {
      // setEasterEggModal(true)
      createRain()
      setKeySuite([])
    }
  }, [keySuite])

  const EasterEggModal = () => {
    return (
      <Modal isOpen={easterEggModal} onCloseModal={() => setEasterEggModal(false)}>
        <ModalBody>Something funny here</ModalBody>
      </Modal>
    )
  }
  /**/ ///////////// */

  useEffect(() => {
    if (pathname.startsWith('/ca/')) {
      const formattedSearchParams = searchParams.toString()
      navigate(`/analytics${pathname}?${formattedSearchParams}${hash}`)
    }
  }, [hash, navigate, pathname, searchParams])

  /** Handlers */

  const handleCloseModal = () => setChangeLogModalIsOpen(false)

  const handleCloseBanner = () => {
    setIsBannerOpen(false)
    localStorage.setItem(LOCAL_STORAGE.STABILITY_BANNER, 'false')
  }

  /** Render */

  return (
    <>
      <TokenHandler />
      {accessTokens ? (
        <div id="root-app-container">
          {isLoading ? (
            <MwMLoader category="general" />
          ) : user ? (
            <>
              <div className={classNames('stability-banner', { 'stability-banner--open': isBannerOpen })}>
                <span>
                  We need your feedback! Please report any suggestions or issues or you can experience on the platform
                  using the feedback button on the right.
                </span>
                <img height={50} src={PipBoy} width={50} />
                <Button className="close-btn" ghost onClick={handleCloseBanner}>
                  <MdOutlineClear color="#626262" size={17} />
                </Button>
              </div>
              <AuthorizationsProvider user={user}>
                <AppSummaryProvider>
                  <SearchParamsProvider>
                    <ToastContainer
                      autoClose={6000}
                      closeOnClick
                      hideProgressBar
                      pauseOnFocusLoss={false}
                      pauseOnHover
                      position="bottom-center"
                      stacked
                      theme="colored"
                    />
                    <NavBar />
                    <Router />
                    <DeprecationBanner popupOnly />
                  </SearchParamsProvider>
                </AppSummaryProvider>
              </AuthorizationsProvider>
              <ChangeLogModal
                isOpen={changeLogModalIsOpen}
                onClose={handleCloseModal}
                pageId={pageId}
                version={myPackage.version}
              />
              <EasterEggModal />
              <FeedbackButton />
            </>
          ) : null}
          {!isProduction() && <ReactQueryDevtools buttonPosition="bottom-left" />}
        </div>
      ) : (
        <Login />
      )}
    </>
  )
}

export default App
